/**
 * LFOModule — a sub-audio-rate OscillatorNode, published as a modulation source.
 *
 * The oscillator's output passes through an output gain (unity by default) so
 * the ModRouter can connect `modOutput` once and route from there. Depth is
 * handled per-connection by the router (not on the LFO).
 *
 * Sync modes:
 *   - 'free'  — Rate knob drives osc.frequency directly (Hz).
 *   - division — frequency derived from transport.bpm × musical division.
 *     Rate knob is ignored while a division is active; changing the Sync
 *     selector back to 'free' reinstates the Rate binding.
 *
 * A Transport reference is optional at construction time; if absent, Sync
 * behaves as 'free' regardless of its setting (so the LFO still works in
 * a transport-less test or preset-load flow).
 */

import type { AudioEngine } from '../AudioEngine';
import { EnumParameter, NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';
import { SYNC_MODES, type SyncMode, type Transport } from '../Transport';

export type LFOWave = 'sine' | 'triangle' | 'sawtooth' | 'square';

const LFO_WAVES = ['sine', 'triangle', 'sawtooth', 'square'] as const;

export class LFOModule implements SynthModule {
  readonly kind = 'modulation' as const;
  readonly type = 'lfo' as const;
  readonly displayName = 'LFO';
  readonly id: string;
  bypass = false;

  readonly waveform = new EnumParameter<LFOWave>({
    name: 'Wave',
    default: 'sine',
    options: LFO_WAVES,
    formatter: (v) =>
      ({ sine: 'SIN', triangle: 'TRI', sawtooth: 'SAW', square: 'SQR' })[v],
  });
  readonly rate = new NumericParameter({
    name: 'Rate',
    default: 2,
    min: 0.01,
    max: 20,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    // Gentle high bias — keeps dice rolls in the audibly-moving 0.5–8 Hz range
    // more often than slow pad drift.
    randomSkew: 1.5,
    formatter: (v) => (v < 1 ? v.toFixed(2) : v.toFixed(1)),
  });
  readonly syncMode = new EnumParameter<SyncMode>({
    name: 'Sync',
    default: 'free',
    options: SYNC_MODES,
    randomizable: false, // dice-rolling between free and 1/16T makes no sense
    formatter: (v) => (v === 'free' ? 'FREE' : v),
  });

  readonly parameters: readonly AnyParameter[] = [
    this.waveform,
    this.syncMode,
    this.rate,
  ];

  readonly input: AudioNode | null = null;
  readonly output: AudioNode | null = null;

  private osc: OscillatorNode | null = null;
  private outputGain: GainNode | null = null;
  private engine: AudioEngine | null = null;
  private readonly transport: Transport | null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string, transport?: Transport | null) {
    this.id = id ?? mintId('lfo');
    this.transport = transport ?? null;
  }

  get modOutput(): AudioNode | null {
    return this.outputGain;
  }

  /**
   * Expose the LFO oscillator's frequency AudioParam so another mod source
   * (S&H, macro) can modulate the LFO's rate at audio rate. Only 'Rate'
   * is addressable — Wave and Sync are enums (no AudioParam possible).
   */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Rate') return this.osc?.frequency ?? null;
    return null;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.osc = ctx.createOscillator();
    this.osc.type = this.waveform.value;
    this.osc.frequency.value = this.computeFrequency();

    this.outputGain = ctx.createGain();
    this.outputGain.gain.value = 1;

    this.osc.connect(this.outputGain);
    this.osc.start();
    this.started = true;

    this.applySyncBindings();

    this.unsubs.push(
      this.waveform.subscribe((v) => {
        if (this.osc) this.osc.type = v;
      }),
    );
    this.unsubs.push(this.syncMode.subscribe(() => this.applySyncBindings()));
    if (this.transport) {
      this.unsubs.push(
        this.transport.bpm.subscribe(() => {
          if (this.syncMode.value !== 'free') this.writeSyncedFrequency();
        }),
      );
    }
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    if (this.osc) {
      try {
        this.osc.stop();
      } catch {
        /* already stopped */
      }
      if (this.osc) this.rate.unbindAudioParam(this.osc.frequency);
      this.osc.disconnect();
      this.osc = null;
    }
    this.outputGain?.disconnect();
    this.outputGain = null;
    this.engine = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  /**
   * Current intended frequency in Hz — Rate knob value in 'free' mode, or
   * BPM × division in a synced mode (falls back to Rate if no transport).
   */
  private computeFrequency(): number {
    if (this.syncMode.value === 'free' || !this.transport) return this.rate.value;
    return this.transport.divisionToHz(this.syncMode.value);
  }

  /**
   * Bind Rate to osc.frequency when free; unbind and write the synced value
   * when a division is active. Re-entrant: safe to call from any subscribe.
   */
  private applySyncBindings(): void {
    if (!this.osc || !this.engine) return;
    const free = this.syncMode.value === 'free' || !this.transport;
    if (free) {
      // Bind (idempotent — Parameter.audioParams is a Set) and push the
      // current Rate to recover from a previous synced write.
      this.rate.bindAudioParam(this.osc.frequency, this.engine.context);
      this.rate.set(this.rate.value, 'init');
    } else {
      this.rate.unbindAudioParam(this.osc.frequency);
      this.writeSyncedFrequency();
    }
  }

  /** Schedule the synced frequency onto osc.frequency with smoothing. */
  private writeSyncedFrequency(): void {
    if (!this.osc || !this.engine) return;
    const hz = this.computeFrequency();
    const t = this.engine.context.currentTime;
    this.osc.frequency.cancelScheduledValues(t);
    this.osc.frequency.setTargetAtTime(hz, t, 0.02);
  }
}
